import React, {createRef, useContext, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import __ from "lodash";
import axios from "axios";
import MaterialTable from "@material-table/core";
import {MenuItem, Select, TablePagination, useTheme} from "@material-ui/core";
import SystemContext from "../../../SystemContext";
import {formatChars} from "../../../blocks/FormatCharacters";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import {
  defaultComponents,
  defaultComponentsScroll,
  MaterialTableBody,
  MaterialTableBodyRow
} from '../../../controls/Material-Table-Components';
import {
  ComboSelect,
  MaskedNumber, MaskedText, MaskedTime,
} from "../../../controls";
import CurrencyLookup from "../../Currencies/components/CurrencyLookup";
import NotesDialog from "../../../controls/Notes/NotesDialog";
import NotesActionButton from "../../../controls/Notes/NotesActionButton";
import CostCategoryLinesListValidator from "../validators/CostCategoryLinesListValidator";
import { useCostCategoryLines, useAddCostCategoryLine, useUpdateCostCategoryLine, useDeleteCostCategoryLine } from "../../../services/costings";
import {DragDropContext, Draggable, Droppable} from "@react-forked/dnd";
import useDimensions from "../../../blocks/useDimensions";


const apiSitePrefix = getApiSitePrefix();
const directions = [
  {code: "", label: "Not Applicable"},
  {code: "OUT", label: "Outbound"},
  {code: "IN", label: "Inbound"}
];

const cargoHandlingTypes = [
  {code: "NONE", label: "None"},
  {code: "BOTH", label: "Both"},
  {code: "LOADING", label: "Loading"},
  {code: "UNLOADING", label: "Unloading"},
  {code: "LOADING ONLY", label: "Loading Only"},
  {code: "UNLOADING ONLY", label: "Unloading Only"},
  {code: "ANY", label: "Any"},
];

const CostCategoryLinesList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const validatorColumn = ValidatorColumn(CostCategoryLinesListValidator, true);

  const props = {
    ..._props
  }
  const classes = props.classes;

  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null,
  });
  const costCategoryUuid = props.costCategoryUuid;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddCostCategoryLine = useAddCostCategoryLine();
  const handleUpdateCostCategoryLine = useUpdateCostCategoryLine();
  const handleDeleteCostCategoryLine = useDeleteCostCategoryLine();

  const queryClient = useQueryClient();
  const extraKeys = [costCategoryUuid];

  const costCategoryLines = useCostCategoryLines({tableRef, queryClient, extraKeys});

  const updatePriorities = async (changedRecords) => {
    SystemData.toggleLoadingProgress(true);
    let response = null;
    const errorText = `An unexpected error has occurred re-ordering the cost items.`;
    try {
      response = await axios.put(`${apiSitePrefix}/costcategorylines/reorder`, changedRecords);
    } catch (err) {
      SystemData.openAlert(
        "Error",
        errorText
      );
      console.debug(err);
    }
    if (!response) {
      SystemData.openAlert(
        "Error",
        errorText
      );
    } else if (response.error) {
      SystemData.openAlert(
        "Error",
        `Error: ${response.error.errorMessage}`
      );
    }
    queryClient.invalidateQueries(["CostCategoryLines"]);
    SystemData.toggleLoadingProgress(false);
  }

  const onDragEnd = (dragProps) => {
    if (!dragProps) return;
    if (!dragProps.source || !dragProps.destination) return;
    if (dragProps.source.index === dragProps.source.destination) return;
    if (bodyRef.current && bodyRef.current.props && bodyRef.current.props.renderData) {
      let records = [...bodyRef.current.props.renderData];
      let sourceRecord = records.splice(dragProps.source.index,1);
      const part1 = [...records].slice(0,dragProps.destination.index);
      const part2 = [...records].slice(dragProps.destination.index);
      const newRecords = [...part1, ...sourceRecord, ...part2];
      const associatedRecords = newRecords;
      const changes = [];
      for (let i = 0; i < associatedRecords.length; i++) {
        const newDisplayOrder = ((i + 1) * 10);
        if (associatedRecords[i].displayOrder !== newDisplayOrder) {
          changes.push({...associatedRecords[i], displayOrder: newDisplayOrder});
        }
      }
      const key = ["CostCategoryLines", costCategoryUuid];
      const queryData = queryClient.getQueryData(key);
      for (let i = 0; i < changes.length; i++) {
        const {costCategoryLineUuid, displayOrder} = changes[i];
        const queryLine = __.find(queryData, {costCategoryLineUuid});
        if (queryLine) {
          queryLine['displayOrder'] = displayOrder;
        }
      }
      const sortedQueryData = __.sortBy(queryData, ['displayOrder']);
      queryClient.setQueryData(key, sortedQueryData);

      updatePriorities(changes);
    }
  };

  const getHighestDisplayOrder = () => {
    let displayOrder = 10;
    if (bodyRef.current && bodyRef.current.props && bodyRef.current.props.renderData) {
      const records = bodyRef.current.props.renderData;
      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        if (record.displayOrder > displayOrder) {
          displayOrder = record.displayOrder + 10;
        }
      }
    }
    return displayOrder;
  }

  const renderDays = (days) => {
    const allDays = ["1", "2", "3", "4", "5", "6", "7"];
    let daysArray = [".", ".", ".", ".", ".", ".", "."];

    if (days && typeof days === "string" && days.length > 0) {
      Array.from(days).forEach(day => {
        const position = allDays.indexOf(day);
        if (position > -1 && position < 7)
          daysArray[position] = day;
      });
    }

    return daysArray.join("");
  }

  const getColumns = (props) => {
    const fontSize = 12;
    const baseColumn = {
      cellStyle: { fontSize:fontSize, padding: "8px 2px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #f5f5f5" },
      headerStyle: { fontSize:fontSize, padding: "1px 2px"},
      altHeaderStyle: { fontSize:fontSize, padding: "1px 2px", backgroundColor: "#efefef"},
      editable: state.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };

    const defaultEditProps = {
      style:{fontSize: fontSize}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */
      {
        ...baseColumn,
        // ...ColumnWidth(180, undefined, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "Cost Item",
        field: "lineName",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(200)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100, undefined, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "Direction",
        field: "direction",
        initialEditValue: null,
        editComponent: (props) => (
          <div style={{fontSize: fontSize, marginTop: -4}}>
            <Select
              labelId="Direction"
              id="Direction"
              value={props.value}
              onChange={e => {
                props.onChange(e.target.value);
              }}
              inputProps={defaultEditProps}
            >
              {directions.map((item) => {
                  return <MenuItem key={item.code} value={item.code}>{item.label}</MenuItem>;
                })
              }
            </Select>
          </div>

        ),
        render: (rowData) => (<div>{__.find(directions, {code: rowData.direction||""}).label}</div>),
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, undefined, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "Days",
        field: "days",
        initialEditValue: "1234567",
        editComponent: (props) => (
          <MaskedText
            mask={"D".repeat(7)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: (rowData) => (<span>{renderDays(rowData.days)}</span>)
      },
      {
        ...baseColumn,
        ...ColumnWidth(55, undefined, undefined, baseColumn.cellStyle, {...baseColumn.altHeaderStyle, textAlign: "left"}, undefined, false),
        title: "From",
        field: "validTimeFrom",
        groupTitle: "Validity Hours",
        initialEditValue: "00:00",
        editComponent: (props) => (
          <MaskedTime
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: rowData => (
          <div>{rowData.validTimeFrom?.substr(0,Math.min(rowData.validTimeFrom?.length, 5))}</div>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(55, undefined, undefined, baseColumn.cellStyle, {...baseColumn.altHeaderStyle, textAlign: "left"}, undefined, false),
        title: "To",
        field: "validTimeTo",
        groupTitle: "Validity Hours",
        initialEditValue: "23:59",
        editComponent: (props) => (
          <MaskedTime
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: rowData => (
          <div>{rowData.validTimeTo?.substr(0,Math.min(rowData.validTimeTo?.length, 5))}</div>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "Handling Type",
        field: "cargoHandlingType",
        initialEditValue: "IGNORE",
        editComponent: (props) => (
          <div style={{fontSize: fontSize, marginTop: -3}}>
            <ComboSelect
              id="cargoHandlingType"
              group="COSTING CARGO HANDLING TYPES"
              subGroup=""
              fullWidth
              value={props.value || "IGNORE"}
              onChange={props.onChange}
            />
          </div>
        ),
        render: (rowData) => (
          <ComboSelect
            id="cargoHandlingType"
            group="COSTING CARGO HANDLING TYPES"
            subGroup=""
            fullWidth
            value={rowData.cargoHandlingType || "IGNORE"}
            displayOnly={true}
            // onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, undefined, baseColumn.cellStyle, baseColumn.altHeaderStyle, undefined, false),
        title: "Type",
        field: "calculationType",
        groupTitle: "Calculation",
        editComponent: (props) => (
          <div style={{fontSize: fontSize, marginTop: -3}}>
            <ComboSelect
              id="calculationType"
              group="COST CALCULATION TYPES"
              subGroup=""
              fullWidth
              value={props.value || ""}
              onChange={newValue => {
                const currentValue = props.value;
                const data = {
                  ...props.rowData,
                  calculationType: newValue
                }
                if (newValue !== currentValue) {
                  data["calculationUnitType"] = null;
                }
                props.onRowDataChange(data);
              }}
            />
          </div>
        ),
        render: (rowData) => (
          <ComboSelect
            id="calculationType"
            group="COST CALCULATION TYPES"
            subGroup=""
            fullWidth
            value={rowData.calculationType}
            displayOnly={true}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, undefined, baseColumn.cellStyle, baseColumn.altHeaderStyle, undefined, false),
        title: "Unit Type",
        field: "calculationUnitType",
        groupTitle: "Calculation",
        editComponent: (props) => { return(
          <div style={{fontSize: fontSize, marginTop: -3}}>
            <ComboSelect
              id="calculationUnitType"
              group="CALCULATION UNIT TYPES"
              subGroup={props.rowData?.calculationType}
              fullWidth
              value={props.value || ""}
              onChange={props.onChange}
            />
          </div>
        )},
        render: (rowData) => (
          <ComboSelect
            id="calculationUnitType"
            group="CALCULATION UNIT TYPES"
            subGroup={rowData?.calculationType}
            fullWidth
            value={rowData?.calculationUnitType}
            displayOnly={true}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(80, undefined, undefined, {...baseColumn.cellStyle, textAlign: 'right'}, {...baseColumn.headerStyle, textAlign: 'right'}, undefined, false),
        title: "Price",
        field: "price",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <div style={{fontSize: fontSize, marginTop: 1}}>
            <MaskedNumber
              value={props.value}
              decimalScale={2}
              fixedDecimalScale={true}
              style={{ fontSize: fontSize, textAlign: "right" }}
              onChange={props.onChange}
            />
          </div>
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.price}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, undefined, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "Currency",
        field: "currencyCode",
        initialEditValue: "USD",
        editComponent: (props) => (
          <div style={{fontSize: fontSize, marginTop: -2}}>
            <CurrencyLookup
              showChildCurrencies={false}
              value={props.value}
              style={{ fontSize: fontSize, textAlign: "right" }}
              onChange={props.onChange}
            />
          </div>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(50, undefined, undefined, {...baseColumn.cellStyle, textAlign: 'right'}, {...baseColumn.altHeaderStyle, textAlign: 'right'}, undefined, false),
        title: "Min",
        field: "minimumQuantity",
        groupTitle: "Quantity",
        type: "numeric",
        emptyValue: null,
        initialEditValue: 0,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(4)}
            value={props.value}
            decimalScale={0}
            style={{ fontSize: fontSize, textAlign: "right" }}
            onChange={props.onChange}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.minimumQuantity}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, undefined, undefined, {...baseColumn.cellStyle, textAlign: 'right'}, {...baseColumn.altHeaderStyle, textAlign: 'right'}, undefined, false),
        title: "Free",
        field: "freeQuantity",
        groupTitle: "Quantity",
        type: "numeric",
        emptyValue: null,
        initialEditValue: 0,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(8)}
            value={props.value}
            decimalScale={0}
            style={{ fontSize: fontSize, textAlign: "right" }}
            onChange={props.onChange}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.freeQuantity}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, undefined, undefined, {...baseColumn.cellStyle, textAlign: 'right'}, {...baseColumn.altHeaderStyle, textAlign: 'right'}, undefined, false),
        title: "Inbound",
        field: "costAllocationInboundPercentage",
        groupTitle: "Cost Allocation",
        // type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(3)}
            value={props.value}
            decimalScale={0}
            valueCap={100}
            suffix="%"
            style={{ fontSize: fontSize, textAlign: "right" }}
            onChange={(value) => {
              const outbound = 100 - value;
              const data = {
                ...props.rowData,
                costAllocationInboundPercentage: value,
                costAllocationOutboundPercentage: outbound,
              }
              props.onRowDataChange(data);
            }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            suffix="%"
            value={props.costAllocationInboundPercentage}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, undefined, undefined, {...baseColumn.cellStyle, textAlign: 'right'}, {...baseColumn.altHeaderStyle, textAlign: 'right'}, undefined, false),
        title: "Outbound",
        field: "costAllocationOutboundPercentage",
        groupTitle: "Cost Allocation",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(3)}
            value={props.value}
            decimalScale={0}
            valueCap={100}
            suffix="%"
            style={{ fontSize: fontSize, textAlign: "right" }}
            onChange={(value) => {
                const inbound = 100 - value;
                const data = {
                  ...props.rowData,
                  costAllocationInboundPercentage: inbound,
                  costAllocationOutboundPercentage: value,
                }
                props.onRowDataChange(data);
            }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            suffix="%"
            value={props.costAllocationOutboundPercentage}
            decimalScale={0}
          />
        )
      },
      // {
      //   ...baseColumn,
      //   // ...ColumnWidth(100, undefined,undefined,undefined, {width: 76},undefined,false),
      //   title: "Notes",
      //   field: "notes",
      //   initialEditValue: "",
      //   editable: state.canEdit ? "always" : "never",
      // },

      // {
      //   ...baseColumn,
      //   title: "",
      // },
    ];
  };

  const [tableContainerRef, { width: tableWidth, height: tableHeight}] = useDimensions({});
  const theader = tableContainerRef.current?.querySelector('thead');
  const thead = theader ? theader.clientHeight:36;
  const bodyHeight = (tableHeight - thead) - 14 ;

  const bodyRef = createRef();

  return (isAuthenticated &&
    <div  className={state.canEdit?classes.tariffBandListEdit:classes.tariffBandList}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          backgroundColor: "#fdfdfd",
          border: "2px solid #aaaaaa",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.childTableHeader.backgroundColor,
            color: theme.palette.childTableHeader.textColor,
            padding: "2px",
          },
          headerRowStyle: {
            // display: "table",
            width: "100%",
          },
          showTitle: true,
          search: false,
          toolbarButtonAlignment: "right",
          padding: "dense",
          paging: false,
          // minBodyHeight: "300", //168+60//FIXME to calculate dynamic height, needed for correct scroll position identification
          // maxBodyHeight: "200"
          addRowPosition: "first"
        }}
        components={{
          ...defaultComponents,
          Body: (props) => (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable
                droppableId={"singleColumnDroppableAreaThusStaticInput"}
              >
                {(provided) => (
                  <MaterialTableBody
                    ref={bodyRef}
                    {...props}
                    forwardedRef={provided.innerRef}
                    placeHolder={provided.placeholder}
                    rowHeight={40}
                    overscanRowCount={1000}
                    addVirtualScrolling={true}
                    bodyHeight={bodyHeight}
                    headerHeight={thead}
                    filterRowHeight={40}
                  />
                )}
              </Droppable>
            </DragDropContext>
          ),
          Row: (props) => (
            <Draggable
              draggableId={props.data.tableData.id.toString()}
              index={props.data.tableData.id}
            >
              {(provided) => {
                return (
                  <MaterialTableBodyRow
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    {...props}
                    forwardedRef={provided.innerRef}
                  />
                );
              }}
            </Draggable>
          ),
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        actions={[
          (rowData) => (
            {
              hidden: false,
              disabled: !state.canEdit,
              icon: () => (
                <NotesActionButton
                  canEdit={true}
                  primaryObjectUuid={rowData?.costCategoryLineUuid}
                  noteType="COST CATEGORY:LINE"
                  title="Cost Item Notes"
                  eventPrefix="CostCategoryLineNoteDialog"
                  style={{ color: `${state.canEdit ? theme.palette.primary.main : "#cecece"}`, padding: 0 }}
                />
              ),
              isFreeAction: false,
            }
          ),
        ]}
        title="Cost Items"
        columns={getColumns({canEdit: state.canEdit})}
        data={costCategoryLines.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: state.canEdit
              ? (newData) => new Promise((resolve, reject) => {
                const cargoHandlingType = newData.cargoHandlingType === "IGNORE" ? null : newData.cargoHandlingType;
                handleAddCostCategoryLine.mutate({record: {...newData, costCategoryUuid: costCategoryUuid, displayOrder: getHighestDisplayOrder(), cargoHandlingType}, resolve, reject, extraKeys});
              })
              : undefined,
          onRowUpdate: state.canEdit
            ? (newData, oldData) =>
              new Promise((resolve, reject) => {
                const cargoHandlingType = newData.cargoHandlingType === "IGNORE" ? null : newData.cargoHandlingType;
                handleUpdateCostCategoryLine.mutate({record: {...newData, cargoHandlingType}, oldRecord: oldData, resolve, reject, extraKeys});
              })
            : undefined,
          onRowDelete: state.canEdit
            ? (oldData) =>
              new Promise((resolve, reject) => {
                handleDeleteCostCategoryLine.mutate({oldRecord: oldData, resolve, reject, extraKeys});
              })
            : undefined,
        }}
      />
      <NotesDialog
        eventPrefix="CostCategoryLineNoteDialog"
        afterSave={undefined}
        onClose={undefined}
        classes={{}}
      />
    </div>
  );

}

export default CostCategoryLinesList;
